// Time window for the audit log. The admin API pages by audit ID only (`before`, docs/api.md 2.5). Audit IDs are
// ULIDs, and a ULID starts with its creation time, so a synthetic ID made from the "to" time is a valid `before`
// cursor. The registry checks only the prefix and the 26-char format (ids.ts isId) and lists `id < before`.

const CROCKFORD = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";

export type AuditPreset = "all" | "1h" | "24h" | "7d" | "30d" | "custom";

export const AUDIT_PRESETS: { value: AuditPreset; label: string; ms?: number }[] = [
  { value: "all", label: "All time" },
  { value: "1h", label: "Last hour", ms: 3_600_000 },
  { value: "24h", label: "Last 24 hours", ms: 86_400_000 },
  { value: "7d", label: "Last 7 days", ms: 7 * 86_400_000 },
  { value: "30d", label: "Last 30 days", ms: 30 * 86_400_000 },
  { value: "custom", label: "Custom (UTC)" },
];

/** The 10-char time part of a ULID for a time in milliseconds. */
export function ulidTime(ms: number): string {
  let n = Math.max(0, Math.floor(ms));
  let out = "";
  for (let i = 0; i < 10; i++) {
    out = CROCKFORD[n % 32] + out;
    n = Math.floor(n / 32);
  }
  return out;
}

/** The time in milliseconds of a prefixed ULID, for example `aud_01J...`, or null if it is not one. */
export function idTime(id: string): number | null {
  const m = /^[a-z]+_([0-9A-HJKMNP-TV-Z]{10})[0-9A-HJKMNP-TV-Z]{16}$/.exec(id);
  if (!m) return null;
  let n = 0;
  for (const ch of m[1]) n = n * 32 + CROCKFORD.indexOf(ch);
  return n;
}

/** `before` cursor that lists every audit entry made at `toMs` or earlier. */
export function auditCursor(toMs: number): string {
  return `aud_${ulidTime(toMs)}${"Z".repeat(16)}`;
}

/** A `datetime-local` value read as UTC, or null when it is empty or not valid. */
export function parseUtcInput(value: string): number | null {
  if (!value) return null;
  const ms = Date.parse(`${value}${value.length === 16 ? ":00" : ""}Z`);
  return Number.isNaN(ms) ? null : ms;
}

export interface AuditWindow {
  from: number | null;
  to: number | null;
}

/** The window of a preset at time `now`, or of the custom from and to inputs. Returns an error text if invalid. */
export function auditWindow(
  preset: AuditPreset,
  now: number,
  custom: { from: string; to: string },
): AuditWindow | { error: string } {
  if (preset === "all") return { from: null, to: null };
  if (preset === "custom") {
    const from = parseUtcInput(custom.from);
    const to = parseUtcInput(custom.to);
    if (custom.from && from === null) return { error: "The From time is not valid." };
    if (custom.to && to === null) return { error: "The To time is not valid." };
    if (from !== null && to !== null && from >= to) return { error: "From must be before To." };
    return { from, to };
  }
  const ms = AUDIT_PRESETS.find((p) => p.value === preset)?.ms ?? 0;
  return { from: now - ms, to: null };
}

/** True if an entry made at `created` (RFC 3339) is inside the window. */
export function inWindow(created: string, w: AuditWindow): boolean {
  const t = Date.parse(created);
  if (Number.isNaN(t)) return true;
  return (w.from === null || t >= w.from) && (w.to === null || t <= w.to);
}
